import {
  Alert,
  Box,
  CircularProgress,
  Typography,
  useTheme,
} from "@mui/material";
import { Link, useParams } from "react-router";

import { useAuth } from "@/providers";
import { PageBreadcrumbs, PageLayout } from "@/shared/components";
import { Invocations } from "@/shared/components/actions/Invocations";
import { TriggerDispatchesForTrigger } from "@/shared/components/TriggerDispatches";
import { type OrgRecord } from "@/shared/domain/orgs";
import { type TriggerRecord } from "@/shared/domain/triggers";
import { useTrigger } from "@/shared/domain/triggers/hooks";

import { useTriggerRouteContext } from "../useTriggerRouteContext";

import styles from "./Trigger.module.css";
import { TriggerDetailTabBar } from "./TriggerDetailTabBar";
import {
  triggerDetailTabs,
  type TriggerDetailTab,
  type TriggerDetailTabName,
} from "./triggerDetailTabs";
import { TriggerOtherOrgBanner } from "./TriggerOtherOrgBanner";
import { TriggerOverview } from "./TriggerOverview";
import { TriggerThreads } from "./TriggerThreads";

export function Trigger() {
  // The page is both the index route under `/triggers/:trigger` (the
  // Details tab, no splat) and its `*` route, whose splat is the tab segment.
  const tabSegment = useParams<{ "*": string }>()["*"] ?? "";

  const theme = useTheme();
  const { currentOrganization } = useAuth();

  const { triggerId } = useTriggerRouteContext();
  const triggerQuery = useTrigger(triggerId);
  const trigger = triggerQuery.data;

  const Breadcrumbs = (
    <PageBreadcrumbs>
      <Link to="/triggers">Triggers</Link>
      {trigger !== undefined && <span>{trigger.name}</span>}
    </PageBreadcrumbs>
  );

  if (triggerQuery.isLoading) {
    return (
      <PageLayout title={"Roboto - Trigger"} headerContentLeft={Breadcrumbs}>
        <CircularProgress />
      </PageLayout>
    );
  }

  let errorDisplay = null;
  if (triggerQuery.error !== null) {
    errorDisplay = (
      <Box sx={{ mt: theme.spacing(2), mb: theme.spacing(1) }}>
        <Alert severity="error">{triggerQuery.error.message}</Alert>
      </Box>
    );
  }

  if (!currentOrganization) {
    return (
      <PageLayout title={"Roboto - Trigger"} headerContentLeft={Breadcrumbs}>
        <Typography color={theme.palette.error.main} variant="h4">
          No organization selected
        </Typography>
      </PageLayout>
    );
  }

  const tabs: readonly TriggerDetailTab[] = trigger
    ? triggerDetailTabs(trigger)
    : [];
  // A segment no tab has shows Details, and the URL stays as it is.
  const activeTab: TriggerDetailTab | undefined =
    tabs.find((tab) => tab.name === tabSegment) ?? tabs[0];
  // What the tab says instead of its content, if anything. A tab the trigger
  // has nothing for says what it would hold. Invocations and threads search
  // the selected org, so on another org's trigger they would wrongly say it
  // started nothing.
  const searchesSelectedOrg =
    activeTab?.name === "invocations" || activeTab?.name === "threads";
  const tabNotice =
    activeTab?.unavailableReason ??
    (trigger?.org_id !== currentOrganization.org_id && searchesSelectedOrg
      ? "Switch to this trigger's org to see what it started."
      : null);

  return (
    <PageLayout title={"Roboto - Trigger"} headerContentLeft={Breadcrumbs}>
      {errorDisplay}
      {trigger && activeTab !== undefined && (
        <>
          <TriggerDetailTabBar
            trigger={trigger}
            tabs={tabs}
            activeTab={activeTab.name}
          />
          <Box
            sx={{
              padding: theme.spacing(1, 0),
              paddingBottom: theme.spacing(1),
            }}
          >
            {/* Details shows it inside its own column, so it shares the
                column's width. */}
            {activeTab.name !== "details" && (
              <TriggerOtherOrgBanner
                orgId={trigger.org_id}
                className={styles.otherOrgBanner}
              />
            )}
            {tabNotice !== null ? (
              <Typography color="text.secondary" variant="body2">
                {tabNotice}
              </Typography>
            ) : (
              <TriggerTabContent
                record={trigger}
                tab={activeTab.name}
                currentOrg={currentOrganization}
              />
            )}
          </Box>
        </>
      )}
    </PageLayout>
  );
}

function TriggerTabContent({
  record,
  tab,
  currentOrg,
}: {
  record: TriggerRecord;
  tab: TriggerDetailTabName;
  currentOrg: OrgRecord;
}) {
  switch (tab) {
    case "details":
      return <TriggerOverview record={record} />;
    case "history":
      return (
        <TriggerDispatchesForTrigger
          triggerId={record.trigger_id}
          targets={record.targets}
        />
      );
    case "threads":
      return <TriggerThreads triggerId={record.trigger_id} />;
    case "invocations":
      return (
        <Invocations triggerId={record.trigger_id} currentOrg={currentOrg} />
      );
  }
}
